import * as d3 from 'd3-selection'
import * as array from 'd3-array'

/*
 * Returns the total value used for this infographic container.
 *
 * Receives the axes config object and the total of the points displayed
 * in the graphic.
 */
export const total = (axes, pointsTotal) => {
  let total = null

  if (typeof axes.infographic.total !== 'undefined') {
    // If `axes.infographic.total` is set, we want to use that value, unless it's
    // lower than our actual total
    total = Math.max(pointsTotal, axes.infographic.total)
  } else if (pointsTotal < 1) {
    // If the sum of all series is less than 1, the total is 1.
    total = 1
  } else {
    total = pointsTotal
  }

  return total
}

export const getStackedPoints = (config, totalHeight, block, series, padding, y, width, reverse = true) => {
  let points = series.map((serie, idx) => {
      let point = getPoint(block.range.id, serie)
      point.idx = idx // the stage
      return point
    }).filter(p => p.raw)
    , stageHeight = totalHeight / series.length
    , total = 0

  // This is messy and weird, but we need to reverse the order of the points,
  // so they start at the top and work down.
  if (reverse) {
    points.reverse()
  }

  if (points.length) {
    // Total is the largest stacked point _range value
    total = array.max(points, p => p._range[1])
  }

  // Re-stack calculating their partial values
  let stack = points.reduce((p, c) => {
    let sum = p.length ? p[p.length - 1] : 0
    return p.concat(sum + c.raw)
  }, [])

  points.forEach((point, i, all) => {
    point.offset = i * padding

    if (config.equallySizedStages) {
      // equal height stages
      point.height = i * stageHeight + stageHeight
    } else {
      // Volume based
      point.height = y(stack[i] / total)
    }

    point.width = width(point.height)
  })

  if (reverse) {
    points.reverse() // Return to original order
  }

  return points
}

/*
 * Adds top to bottom linear gradients for each of the colors
 */
export const coloredGradients = (selection, type, colors, stops, transition) => {
  selection.each(function (d) {
    let gradients = d3.select(this).selectAll('linearGradient.colored-gradient')
                      .data(colors, d => d)
      , enter = gradients.enter()
                         .append('linearGradient')
                         .attr('class', 'colored-gradient')

    enter.attr('class', d => `colored-gradient ${type}-colored-gradient  ${d}`)
         .attr('x1', 0) // top to bottom gradient
         .attr('x2', 0)
         .attr('y1', 0)
         .attr('y2', 1)
         .attr('id', d => `${type}-colored-gradient-${d}`)

    stops.forEach(stop => {
      enter.append('stop').attr('offset', stop)
    })

    transition.end().then(() => {
      gradients.exit().remove()
    }).catch(() => {
      // interrupted transition, ignore
    })
  })
}

export const title = (blocks, block, series) => {
  if (blocks.length > 1) {
    return [block.range.title, block.range.subtitle].filter(Boolean).join(' ')
  } else if (series.length == 1) {
    return series[0].title
  } else {
    return null
  }
}

/*
 * Looks up the point in the serie that matches the rangeId. Handles no-breakdown
 * elegantly.
 */
export const getPoint = (rangeId, serie) => {
  let point = {}
  if (rangeId == 'calculated-range') {
    point = Object.assign(point, serie.points[0]) // First one, no real breakdown
  } else {
    point = Object.assign(point, serie.points.find(p => p.rangeId == rangeId))
  }
  point.clickable = serie.clickable
  point.serieId = serie.id
  point.color = point.color || serie.color
  point.title = serie.title
  point.subtitle = serie.subtitle // description
  point.formatter = serie.formatter || (d => d)
  point.deemphasize = serie.deemphasize
  return point
}
